// Reusable confirmation dialog. Replaces browser-native window.confirm()
// with a branded modal. Use via <ConfirmDialog open onConfirm ...>.
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

export default function ConfirmDialog({
  open, title, body, confirmLabel, cancelLabel, danger = true, onConfirm, onCancel, testid,
}) {
  const { t } = useI18n();
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md grid place-items-center p-4"
          onClick={onCancel}
          data-testid={testid || "confirm-dialog"}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-start gap-4">
              <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${danger ? "bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]" : "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"}`}>
                <AlertTriangle size={18} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))]">{title || t("confirm_delete_title")}</h3>
                {body && <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">{body}</p>}
              </div>
              <button onClick={onCancel} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] shrink-0" aria-label={t("close")}><X size={14} /></button>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={onCancel} className="btn btn-quiet" data-testid={`${testid || "confirm"}-cancel`}>{cancelLabel || t("cancel")}</button>
              <button onClick={onConfirm} className={`btn btn-primary ${danger ? "!bg-[hsl(var(--danger))]" : ""}`} data-testid={`${testid || "confirm"}-yes`}>{confirmLabel || t("delete")}</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
